/** `RefundStatus` common codes (`categoryname` = RefundStatus). */
export const REFUND_STATUS_CODE = {
  ADDED: 1,
  CREATED: 2,
  PROCESSED: 3,
  FAILED: 4,
} as const;

/** Minimal refund row fields used for deposit breakdown. */
export type RefundRowForDepositBreakdown = {
  refundableamount: number;
  bookingamount: number;
  /** Refund status code (`RefundStatus` category), stored as `paymentstatus` on API row. */
  paymentstatus: number;
  refundinitiatedon: string | null;
  razorpayrefundid: string | null;
};

export type BreakdownCaption = {
  text: string;
  color: "success.main" | "error.main" | "text.secondary";
};

export type SecurityDepositBreakdown = {
  totalPayment: number;
  /** Live value from the refund form (updates when the user edits refundable amount). */
  refundableAmount: number;
  /** Set when refund was processed or recorded on the row. */
  amountRefunded: number | null;
  isRefundProcessed: boolean;
  /** Original refundable amount from API — does not change when the form amount is edited. */
  proposedRefundAmount: number | null;
  refundStatusCode: number;
  breakdownCaption: BreakdownCaption | null;
};

function pickNumber(
  raw: Record<string, unknown>,
  keys: string[],
): number | null {
  for (const key of keys) {
    const v = raw[key];
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

export function isRefundConsideredProcessed(
  normalized: RefundRowForDepositBreakdown,
): boolean {
  return normalized.paymentstatus === REFUND_STATUS_CODE.PROCESSED;
}

export function getRefundBreakdownCaption(
  refundStatusCode: number,
  proposedRefundAmount: number | null,
): BreakdownCaption | null {
  switch (refundStatusCode) {
    case REFUND_STATUS_CODE.PROCESSED:
      return null;
    case REFUND_STATUS_CODE.FAILED:
      return {
        text: "Refund processing failed. The refunded amount may not reflect the final settlement. You can retry again to proceed with the refund.",
        color: "error.main",
      };
    case REFUND_STATUS_CODE.CREATED:
      return {
        text:
          proposedRefundAmount != null
            ? "Refund is created and pending processing; the amount refunded will update once processed."
            : "Refund is created and pending processing.",
        color: "text.secondary",
      };
    case REFUND_STATUS_CODE.ADDED:
      return {
        text:
          proposedRefundAmount != null
            ? "Refund is added; the amount refunded will update after processing."
            : "Refund is added for this booking.",
        color: "text.secondary",
      };
    default:
      return proposedRefundAmount != null
        ? {
            text: "Refund is not completed yet; the amount refunded will update after processing.",
            color: "text.secondary",
          }
        : null;
  }
}

function isPaymentRefundAlreadyAdded(payment: Record<string, unknown>): boolean {
  const v =
    payment.isrefundadded ??
    payment.isRefundAdded ??
    payment.IsRefundAdded;
  return (
    v === true ||
    v === 1 ||
    (typeof v === "string" && v.trim().toLowerCase() === "true")
  );
}

/** Read-only breakdown for payment details (before / after add-refund). */
export function buildSecurityDepositBreakdownFromPayment(
  payment: Record<string, unknown>,
): SecurityDepositBreakdown {
  const totalPayment =
    pickNumber(payment, [
      "amount",
      "bookingamount",
      "bookingAmount",
      "totalpayment",
      "totalPayment",
      "totalpayableamount",
    ]) ?? 0;

  const refundableAmount =
    pickNumber(payment, [
      "refundableamount",
      "refundableAmount",
      "originalrefundableamount",
      "originalRefundableAmount",
    ]) ?? 0;

  const refundAlreadyAdded = isPaymentRefundAlreadyAdded(payment);

  let amountRefunded = pickNumber(payment, [
    "refundedamount",
    "refundedAmount",
    "amountrefunded",
    "amountRefunded",
    "finalrefundamount",
    "finalRefundAmount",
  ]);

  if (
    (amountRefunded == null || amountRefunded <= 0) &&
    refundAlreadyAdded &&
    refundableAmount > 0
  ) {
    amountRefunded = refundableAmount;
  }

  const proposedRefundAmount =
    !refundAlreadyAdded && refundableAmount > 0 ? refundableAmount : null;

  const refundStatusCode = refundAlreadyAdded
    ? REFUND_STATUS_CODE.ADDED
    : 0;

  let breakdownCaption: BreakdownCaption | null = null;
  if (refundAlreadyAdded) {
    breakdownCaption = {
      text: "A refund has already been added for this payment.",
      color: "text.secondary",
    };
  } else if (refundableAmount > 0) {
    breakdownCaption = {
      text: "Refundable security deposit is available; use Process Refund to initiate.",
      color: "text.secondary",
    };
  }

  return {
    totalPayment,
    refundableAmount,
    amountRefunded:
      amountRefunded != null && amountRefunded > 0 ? amountRefunded : null,
    isRefundProcessed: false,
    proposedRefundAmount,
    refundStatusCode,
    breakdownCaption,
  };
}

export function buildSecurityDepositBreakdown(
  raw: Record<string, unknown>,
  normalized: RefundRowForDepositBreakdown,
  originalRefundableSnapshot: number,
  editedAmount: string | number,
): SecurityDepositBreakdown {
  const totalPayment =
    pickNumber(raw, [
      "totalpayment",
      "totalPayment",
      "totalpayableamount",
      "totalPayableAmount",
    ]) ??
    (Number.isFinite(normalized.bookingamount) ? normalized.bookingamount : 0);

  const proposedRefundAmount =
    pickNumber(raw, ["originalrefundableamount", "originalRefundableAmount"]) ??
    (originalRefundableSnapshot > 0
      ? originalRefundableSnapshot
      : Number.isFinite(normalized.refundableamount)
        ? normalized.refundableamount
        : 0);

  const parsedEdit = Number(editedAmount);
  const refundableAmount =
    Number.isFinite(parsedEdit) && parsedEdit >= 0
      ? parsedEdit
      : proposedRefundAmount;

  const isRefundProcessed = isRefundConsideredProcessed(normalized);

  let amountRefunded = pickNumber(raw, [
    "refundedamount",
    "refundedAmount",
    "amountrefunded",
    "amountRefunded",
    "finalrefundamount",
    "finalRefundAmount",
    "proposedamount",
    "proposedAmount",
  ]);

  if ((amountRefunded == null || amountRefunded <= 0) && isRefundProcessed) {
    amountRefunded =
      pickNumber(raw, ["proposedamount", "proposedAmount"]) ??
      (Number.isFinite(parsedEdit) && parsedEdit > 0 ? parsedEdit : null) ??
      pickNumber(raw, ["refundableamount", "refundableAmount"]) ??
      normalized.refundableamount;
  }

  const refundStatusCode = normalized.paymentstatus;

  return {
    totalPayment,
    refundableAmount,
    amountRefunded:
      amountRefunded != null && amountRefunded > 0 ? amountRefunded : null,
    isRefundProcessed,
    proposedRefundAmount:
      proposedRefundAmount > 0 ? proposedRefundAmount : null,
    refundStatusCode,
    breakdownCaption: getRefundBreakdownCaption(
      refundStatusCode,
      proposedRefundAmount > 0 ? proposedRefundAmount : null,
    ),
  };
}
